import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PreviewStage } from '../components/PreviewStage';
import { TranscriptPanel } from '../components/TranscriptPanel';
import { TemplatePicker } from '../components/TemplatePicker';
import { StyleControls } from '../components/StyleControls';
import { Transport } from '../components/Transport';
import { Logo } from '../components/Logo';
import { useEditor } from '../state/editorStore';
import { playbackClock } from '../playback/clock';
import { loadDemo } from '../demo/loadDemo';
import { api } from '../api/client';
import {
  uploadAndTranscribe,
  loadTranscriptIntoEditor,
  saveEdits,
  saveAndExport,
  NeedsAuthError,
  NeedsProjectError,
} from '../lib/pipeline';

type Tab = 'templates' | 'style';

export function EditorPage() {
  const { projectId } = useParams();
  const [tab, setTab] = useState<Tab>('templates');
  const [leftOpen, setLeftOpen] = useState(true);
  const [safeZones, setSafeZones] = useState(false);
  const [aspect, setAspect] = useState('9:16');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stage, setStage] = useState<string | null>(null);
  const [exportStage, setExportStage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const saveState = useEditor((s) => s.saveState);
  const undo = useEditor((s) => s.undo);
  const redo = useEditor((s) => s.redo);
  const markSaved = useEditor((s) => s.markSaved);
  const fileRef = useRef<HTMLInputElement>(null);

  // Load the project's REAL transcript from the API when opened at
  // /editor/:projectId; otherwise fall back to the built-in demo transcript.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!projectId) {
        loadDemo(null);
        return;
      }
      try {
        const t = await api.getTranscript(projectId);
        if (cancelled) return;
        useEditor.setState({ projectId, language: t.language });
        // A real project with no words is a legit silent/no-audio clip - load its
        // real (empty) transcript and show the video with no captions. Only warn
        // when it's genuinely still transcribing (a transcription row exists and
        // hasn't completed). Never fall back to the built-in demo transcript here.
        if (!t.words.length && t.status && t.status !== 'completed') {
          setLoadError('Transcript not ready yet - still processing.');
        }
        // Prefer the low-res proxy for smooth playback; fall back to the source.
        loadTranscriptIntoEditor(t, t.proxyUrl ?? t.sourceUrl);
      } catch {
        if (cancelled) return;
        setLoadError('Could not load this project - showing the demo transcript.');
        loadDemo(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Debounced autosave: persist edits (words, segments, emphasis, template,
  // style) to the API 800ms after the last change. In demo mode (no project)
  // there is nothing to persist, so just clear the dirty flag locally.
  useEffect(() => {
    if (saveState !== 'dirty') return;
    const id = setTimeout(() => {
      if (!useEditor.getState().projectId) {
        markSaved();
        return;
      }
      saveEdits().catch((err) => {
        // Leave state 'dirty' so the next change (or retry) saves again.
        useEditor.setState({ saveState: 'dirty' });
        if (err instanceof NeedsAuthError) setLoadError('Sign in to save your changes.');
        else setLoadError('Could not save changes - will retry on the next edit.');
      });
    }, 800);
    return () => clearTimeout(id);
  }, [saveState, markSaved]);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA';
      if (typing) return;
      const mod = e.metaKey || e.ctrlKey;
      if (e.code === 'Space') {
        e.preventDefault();
        playbackClock.toggle();
      } else if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (
        mod &&
        ((e.key.toLowerCase() === 'z' && e.shiftKey) || e.key.toLowerCase() === 'y')
      ) {
        e.preventDefault();
        redo();
      } else if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault();
        markSaved();
      } else if (e.key === 'ArrowLeft') {
        playbackClock.seek(playbackClock.timeMs() - 2000);
      } else if (e.key === 'ArrowRight') {
        playbackClock.seek(playbackClock.timeMs() + 2000);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, markSaved]);

  const onPickVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoadError(null);
    try {
      await uploadAndTranscribe(file, setStage);
    } catch (err) {
      setStage(null);
      if (err instanceof NeedsAuthError) {
        setLoadError('Sign in to upload & transcribe your video (top-right).');
      } else {
        setLoadError(err instanceof Error ? err.message : 'Upload failed');
      }
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Load the last completed render so the download button works immediately.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    api
      .getLatestRender(projectId)
      .then((r) => !cancelled && setDownloadUrl(r.url))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const onSave = async () => {
    setLoadError(null);
    try {
      await saveEdits();
    } catch (err) {
      if (err instanceof NeedsProjectError) setLoadError('Upload a video first, then Save.');
      else setLoadError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  // Export ALWAYS starts a new render (the download button grabs the last one).
  const onExport = async () => {
    setLoadError(null);
    try {
      const url = await saveAndExport(aspect, setExportStage);
      setDownloadUrl(url);
      window.open(url, '_blank'); // best-effort auto-open the fresh render
    } catch (err) {
      setExportStage(null);
      if (err instanceof NeedsProjectError) setLoadError('Upload a video first to export.');
      else setLoadError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  return (
    <div className="editor">
      <header className="editor-top">
        <div className="brand">
          <Logo />
        </div>
        <div className="top-actions">
          <select
            value={aspect}
            onChange={(e) => setAspect(e.target.value)}
            aria-label="Aspect ratio"
          >
            <option value="9:16">9:16</option>
            <option value="16:9">16:9</option>
            <option value="1:1">1:1</option>
            <option value="4:5">4:5</option>
          </select>
          <label className="toggle">
            <input
              type="checkbox"
              checked={safeZones}
              onChange={(e) => setSafeZones(e.target.checked)}
            />{' '}
            Safe zones
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            hidden
            onChange={onPickVideo}
          />
          <button onClick={() => fileRef.current?.click()} disabled={Boolean(stage)}>
            {stage ? 'Working…' : 'Upload & transcribe'}
          </button>
          {stage && (
            <span className="save-state" style={{ color: 'var(--accent)' }}>
              {stage}
            </span>
          )}
          {loadError && (
            <span className="save-state dirty" title={loadError}>
              ⚠ {loadError}
            </span>
          )}
          <span className={`save-state ${saveState}`}>
            {saveState === 'saved'
              ? 'Saved'
              : saveState === 'saving'
                ? 'Saving…'
                : 'Changes pending'}
          </span>
          <button onClick={onSave} disabled={Boolean(exportStage)}>
            Save
          </button>
          <button className="primary" onClick={onExport} disabled={Boolean(exportStage)}>
            {exportStage ?? 'Export'}
          </button>
          {downloadUrl && (
            <a
              className="icon-btn"
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              download
              title="Download last render"
              aria-label="Download last render"
            >
              ⭳
            </a>
          )}
        </div>
      </header>

      <div className={leftOpen ? 'editor-body' : 'editor-body left-collapsed'}>
        {leftOpen ? (
          <aside className="left">
            <div className="left-head">
              <div className="tabs">
                <button
                  className={tab === 'templates' ? 'on' : ''}
                  onClick={() => setTab('templates')}
                >
                  Templates
                </button>
                <button className={tab === 'style' ? 'on' : ''} onClick={() => setTab('style')}>
                  Customize
                </button>
              </div>
              <button
                className="collapse"
                onClick={() => setLeftOpen(false)}
                title="Collapse panel"
              >
                «
              </button>
            </div>
            {tab === 'templates' ? <TemplatePicker /> : <StyleControls />}
          </aside>
        ) : (
          <button className="rail" onClick={() => setLeftOpen(true)} title="Expand panel">
            »
          </button>
        )}

        <main className="center">
          <PreviewStage aspect={aspect} showSafeZones={safeZones} />
          <Transport />
        </main>

        <section className="right">
          <TranscriptPanel />
        </section>
      </div>
    </div>
  );
}
